// Design-system primitives. Styling lives in uiCss.js (sy-* classes on theme tokens);
// these components only pick the class names, so they stay small and consistent.
import React, { useEffect, useRef, useState } from "react";
import { recordUiEvent } from "../../lib/logRingBuffer";
import { onToast } from "../../lib/ui/toast.js";
import { ui } from "../../lib/telemetry/instrument.js";
import { IconAlert, IconCheck, IconClose, IconInfo } from "./Icons.jsx";

const cx = (...parts) => parts.filter(Boolean).join(" ");

// The caller names the control (`{...ui("<id>")}` where it is used, which the telemetry registry
// checks); the button carries that id on.
const forwardUi = (rest) => {
  const { "data-ui": id, "data-ui-key": key, ...others } = rest;
  return [id, key, others];
};

/**
 * A button. `variant`: "secondary" (default) | "primary" | "ghost" | "danger"; `size`: "sm" | "md" | "lg".
 * @param {object} props
 */
export function Button({
  variant = "secondary",
  size = "md",
  icon,
  iconRight,
  className,
  children,
  type = "button",
  ...props
}) {
  const [uiId, uiKey, rest] = forwardUi(props);
  return (
    <button
      type={type}
      className={cx(
        "sy-btn",
        variant !== "secondary" && `sy-btn--${variant}`,
        size !== "md" && `sy-btn--${size}`,
        className,
      )}
      {...rest}
      {...(uiId && ui(uiId, uiKey))}
    >
      {icon}
      {children}
      {iconRight}
    </button>
  );
}

/** A square icon-only button; `label` is its accessible name and tooltip. */
export function IconButton({ label, className, children, type = "button", ...props }) {
  const [uiId, uiKey, rest] = forwardUi(props);
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      className={cx("sy-iconbtn", className)}
      {...rest}
      {...(uiId && ui(uiId, uiKey))}
    >
      {children}
    </button>
  );
}

/** A small rounded label; pass `onClick` to make it a button. */
export function Chip({ onClick, className, children, ...rest }) {
  if (onClick) {
    const [uiId, uiKey, others] = forwardUi(rest);
    return (
      <button
        type="button"
        onClick={onClick}
        className={cx("sy-chip", className)}
        {...others}
        {...(uiId && ui(uiId, uiKey))}
      >
        {children}
      </button>
    );
  }
  return (
    <span className={cx("sy-chip", className)} {...rest}>
      {children}
    </span>
  );
}

/** An 11px monospace caps label for a section or field. */
export function Kicker({ className, children, ...rest }) {
  return (
    <div className={cx("sy-kicker", className)} {...rest}>
      {children}
    </div>
  );
}

// The light goes round once per SPINNER_TURN_MS at first, easing up to SPINNER_TOP_RATE times that
// pace over SPINNER_RAMP_MS.
const SPINNER_TURN_MS = 1500;
const SPINNER_TOP_RATE = 1.75;
const SPINNER_RAMP_MS = 3000;

/** How lit an arm is `phase` turns past its peak: one smooth pulse a turn. */
const glowAt = (phase) => ((1 + Math.cos(2 * Math.PI * phase)) / 2) ** 2;

/** Turns of the light `t` ms in (t ≤ the ramp), as the pace eases from 1× up to the top rate. */
function turnsAt(t) {
  const u = t / SPINNER_RAMP_MS;
  // ∫ of the pace above 1×, which rises along a smoothstep.
  const extra = SPINNER_RAMP_MS * (SPINNER_TOP_RATE - 1) * (u ** 3 - u ** 4 / 2);
  return (t + extra) / SPINNER_TURN_MS;
}

/** The one loading indicator. */
export function Spinner({ size = "md", label = "Loading" }) {
  // The Sayer mark's three arms lighting in turn, clockwise from the top; nothing spins. Each arm's
  // light is a layer whose opacity is animated, which the browser runs off the main thread, so it
  // stays smooth while the app is busy. The three pulses are a third of a turn apart and always
  // sum to the same brightness, so the light glides round rather than blinking. The speed-up is
  // drawn into the first animation's keyframes rather than adjusted frame by frame.
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el?.animate) return undefined;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const anims = [];
    el.querySelectorAll(".sy-spinner__glow").forEach((glow, k) => {
      const shift = k / 3;
      if (!reduce) {
        const steps = 90;
        const ramp = Array.from({ length: steps + 1 }, (_, i) => ({
          opacity: glowAt(turnsAt((i / steps) * SPINNER_RAMP_MS) - shift),
        }));
        anims.push(glow.animate(ramp, { duration: SPINNER_RAMP_MS, easing: "linear" }));
      }
      const steps = 24;
      const turn = Array.from({ length: steps + 1 }, (_, i) => ({
        opacity: glowAt(i / steps - shift),
      }));
      anims.push(
        glow.animate(turn, {
          duration: reduce ? 3 * SPINNER_TURN_MS : SPINNER_TURN_MS / SPINNER_TOP_RATE,
          // Picks up exactly where the speed-up leaves the light.
          delay: reduce ? 0 : SPINNER_RAMP_MS,
          iterationStart: reduce ? 0 : turnsAt(SPINNER_RAMP_MS) % 1,
          iterations: Infinity,
          easing: "linear",
        }),
      );
    });
    return () => anims.forEach((a) => a.cancel());
  }, []);
  return (
    <span
      ref={ref}
      className={cx(
        "sy-spinner",
        size === "lg" && "sy-spinner--lg",
        size === "sm" && "sy-spinner--sm",
      )}
      role="status"
      aria-label={label}
    >
      {[0, 1, 2].map((k) => (
        <span key={k} className="sy-spinner__arm">
          <span className="sy-spinner__glow" />
        </span>
      ))}
    </span>
  );
}

/** A shimmer placeholder of the given size. */
export function Skeleton({ width = "100%", height = 12, style }) {
  return (
    <span
      className="sy-skeleton"
      style={{ display: "block", width, height, ...style }}
      aria-hidden="true"
    />
  );
}

const TOAST_ICON = { error: IconAlert, success: IconCheck, info: IconInfo };

/** Renders the toasts raised through lib/ui/toast.js. Mount once. */
export function Toaster() {
  const [items, setItems] = useState([]);
  useEffect(
    () =>
      onToast((t) => {
        // Its kind only: a toast's message can quote what the user typed.
        recordUiEvent({ event: "ui.toast", kind: t.kind });
        setItems((xs) => [...xs.slice(-3), t]);
        if (t.ms > 0) setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== t.id)), t.ms);
      }),
    [],
  );
  if (!items.length) return null;
  return (
    // telemetry-unsurfaced: toast (passing messages in the corner; `ui.toast` records each one)
    <div className="sy-toaster" role="status" aria-live="polite">
      {items.map((t) => {
        const Ico = TOAST_ICON[t.kind] || IconInfo;
        return (
          <div key={t.id} className={cx("sy-toast", `sy-toast--${t.kind}`)}>
            <Ico />
            <span>{t.message}</span>
            <button
              type="button"
              className="sy-iconbtn"
              style={{ width: 22, height: 22 }}
              aria-label="Dismiss"
              {...ui("toast.dismiss")}
              onClick={() => setItems((xs) => xs.filter((x) => x.id !== t.id))}
            >
              <IconClose width={14} height={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
